import { v4 as uuidv4 } from 'uuid';
import firebase from './firebase';
import { API_BASE_URL } from './api';
import { getActiveShopId } from './shopContext';

const states = ['Sold', 'For Sale'];
const mailStates = ['not_requested', 'pending', 'submitting', 'accepted', 'unknown', 'configuration_error', 'superseded'];
const operationPattern = /^[A-Za-z0-9_:-]{1,128}$/;
const storageKey = (scope, id) => `autoclever.listing-status.v1.${encodeURIComponent(JSON.stringify([scope, String(id)]))}`;

export const currentListingStatusScope = () => {
  const uid = firebase.auth().currentUser?.uid;
  const shop = getActiveShopId();
  return uid && shop ? JSON.stringify([API_BASE_URL, uid, shop]) : null;
};

const validatePayload = (value) => {
  if (!value || !Number.isSafeInteger(Number(value.id)) || Number(value.id) < 1
    || !states.includes(value.targetStatus) || !['Yes', 'No'].includes(value.sendReleaseRequest)
    || !Number.isSafeInteger(value.expectedRevision) || value.expectedRevision < 0
    || typeof value.operationId !== 'string' || !operationPattern.test(value.operationId)) throw new Error('Invalid saved listing request.');
};

export const normalizeListingStatusResult = (result, payload) => {
  if (!result || result.id !== payload.operationId || String(result.listingId) !== String(payload.id)
    || result.targetStatus !== payload.targetStatus || !states.includes(result.currentStatus)
    || !['applied', 'already_in_state', 'conflict'].includes(result.businessStatus)
    || !Number.isSafeInteger(result.currentRevision) || result.currentRevision < 0
    || !mailStates.includes(result.mail?.status) || typeof result.mail.canRetry !== 'boolean'
    || (result.mail.canRetry && !['pending', 'configuration_error'].includes(result.mail.status))) {
    throw new Error('The listing result could not be confirmed. Check the saved request before making another change.');
  }
  // Persist only bounded business identifiers and statuses, never tokens or the
  // full listings response. Render local copy rather than unbounded mail text.
  return {
    id: result.id, listingId: String(result.listingId), targetStatus: result.targetStatus,
    businessStatus: result.businessStatus, currentStatus: result.currentStatus, currentRevision: result.currentRevision,
    mail: { status: result.mail.status, canRetry: result.mail.canRetry }
  };
};

export const readListingStatusIntent = (scope, id) => {
  if (!scope) throw new Error('Sign in to your shop before changing listing status.');
  try {
    const value = window.localStorage.getItem(storageKey(scope, id));
    if (!value) return null;
    if (value.length > 12000) throw new Error('Invalid receipt.');
    const saved = JSON.parse(value);
    if (saved.version !== 1 || String(saved.payload?.id) !== String(id)) throw new Error('Invalid receipt.');
    validatePayload(saved.payload);
    if (saved.result) normalizeListingStatusResult(saved.result, saved.payload);
    return saved;
  } catch (_error) {
    throw new Error('Saved listing retry information is unavailable. Recover it before starting another status change.');
  }
};

const write = (scope, entry) => {
  const key = storageKey(scope, entry.payload.id);
  const value = JSON.stringify(entry);
  try {
    if (value.length > 12000) throw new Error('Invalid receipt.');
    window.localStorage.setItem(key, value);
    if (window.localStorage.getItem(key) !== value) throw new Error('Receipt changed.');
  } catch (_error) {
    throw new Error('Listing retry information could not be saved. Check this request before making another change.');
  }
  return entry;
};

export const prepareListingStatusIntent = (scope, input) => {
  const previous = readListingStatusIntent(scope, input.id);
  if (input.operationId) {
    if (previous?.payload.operationId !== input.operationId) throw new Error('This saved request changed. Reopen the listing status dialog.');
    return previous;
  }
  if (previous && !previous.result && previous.payload.targetStatus === input.targetStatus
    && previous.payload.sendReleaseRequest === input.sendReleaseRequest
    && previous.payload.ownershipDestination === (input.ownershipDestination || null)) return previous;
  if (previous && !previous.result) {
    throw new Error('Check the saved listing request before choosing a different status.');
  }
  const payload = {
    id: String(input.id), targetStatus: input.targetStatus, expectedRevision: input.expectedRevision,
    sendReleaseRequest: input.sendReleaseRequest, ownershipDestination: input.ownershipDestination || null,
    stockNumber: String(input.stockNumber || ''), operationId: `listing-status:${uuidv4()}`
  };
  validatePayload(payload);
  return write(scope, { version: 1, payload, result: null });
};

export const recordListingStatusResult = (scope, payload, operation) => {
  const result = normalizeListingStatusResult(operation, payload);
  const previous = readListingStatusIntent(scope, payload.id);
  if (previous?.payload.operationId !== payload.operationId) throw new Error('A newer listing request replaced this response. Reopen the listing status dialog.');
  write(scope, { ...previous, result });
  return result;
};

export const listingMailStatusText = (mail) => ({
  not_requested: 'Title-release email was not requested.',
  pending: 'Title-release email is pending. Retry this saved request to check or continue it.',
  submitting: 'Title-release email is being submitted. Check this request; do not send another copy.',
  accepted: 'The mail service accepted the title-release email. Delivery is not confirmed.',
  unknown: 'The title-release email outcome is unknown. An administrator must review it before another copy is sent.',
  configuration_error: 'The title-release email has not been sent because mail configuration is unavailable. Retry this request after configuration is corrected.',
  superseded: 'The title-release email was superseded by a later inventory change.'
}[mail?.status] || 'The title-release email outcome has not been confirmed.');

export const emitListingStatusRequest = ({ connection, payload, signal, isCurrent }) => new Promise((resolve, reject) => {
  const cleanup = () => {
    clearTimeout(timer);
    connection.off('setListingStatusV2Success', success);
    connection.off('setListingStatusV2Error', failure);
    connection.off('disconnect', disconnect);
    signal.removeEventListener('abort', abort);
  };
  const fail = (message) => { cleanup(); reject(new Error(message)); };
  const success = (response) => {
    if (response?.requestId !== payload.requestId) return;
    if (!isCurrent()) { fail('Account or connection changed. Reopen this listing.'); return; }
    cleanup(); resolve(response);
  };
  const failure = (response) => {
    if (response?.requestId !== payload.requestId) return;
    fail(isCurrent() ? response?.message || 'Unable to confirm listing status.' : 'Account changed. Reopen this listing.');
  };
  const disconnect = () => fail('Connection lost. Reconnect and check the saved listing request; do not start another change.');
  const abort = () => fail('Listing request cancelled locally. Its saved intent remains available for checking.');
  const timer = setTimeout(() => fail('Listing status was not confirmed. Check the saved request after reconnecting; a matching service version is required.'), 10000);
  connection.on('setListingStatusV2Success', success);
  connection.on('setListingStatusV2Error', failure);
  connection.on('disconnect', disconnect);
  signal.addEventListener('abort', abort);
  if (signal.aborted || !isCurrent()) { abort(); return; }
  // Use the captured connected socket, never its reconnect-buffering wrapper.
  try { connection.emit('setListingStatusV2', payload); } catch (error) { fail(error.message); }
});
